import { useUserStore } from "@/stores";
import { Card, Col, Row } from "antd";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { CameraOutlined, FileSearchOutlined, ScanOutlined } from "@ant-design/icons";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import "./Forms.scss";
import { Loader } from "@/layouts/signed-in/Loader/Loader";

const Forms = () => {
	const navigate = useNavigate();
	const { user } = useUserStore();
	const hasDataCollection = useFeatureFlag("data_collection");

	if (!user?.organization) {
		return <Loader />;
	}

	// Memoize forms and track changes more precisely
	const forms = useMemo(() => {
		return user.organization?.forms || [];
	}, [user.organization?.forms]);

	// Check if user has Plus subscription or trial access
	const hasVisionCaptureAccess =
		user?.hasPlusSubscription ||
		user?.is_trial ||
		user?.isTrial ||
		user?.isTrialAccount;

	// Check if this is org 28 (they use different form IDs)
	const isOrg28 = user?.organization?.id === 28;

	// Organization logo (used by capture cards)
	const orgConfig = user.organization?.config as any;
	const orgLogoUrl = orgConfig?.logoUrl || user.organization?.config?.logo_url;

	return (
		<div className="forms-container">
			<div className="forms-page-header">
				<h1>Vision Capture</h1>
				<p>AI-powered capture tools for assets and field data.</p>
			</div>

			<Card className="forms-card" bordered={false}>
				{forms.length === 0 && !hasVisionCaptureAccess && !hasDataCollection ? (
					<p>No forms available for this organization.</p>
				) : (
					<Row gutter={[16, 16]}>
						{/* ─── Asset Capture Card ─── */}
						{hasVisionCaptureAccess &&
							!isOrg28 && (
								<Col xs={24} sm={12} md={8} lg={6}>
									<Card
										hoverable
										onClick={() =>
											navigate("/slim-form/capture", {
												state: { from: "/forms" },
											})
										}
										className="capture-card capture-card-asset"
										cover={
											<div className="capture-card-cover capture-cover-asset">
												<CameraOutlined className="capture-card-icon" />
											</div>
										}
									>
										<Card.Meta
											title="Asset Capture"
											description="Capture assets using AI-powered photo recognition"
										/>
									</Card>
								</Col>
							)}

						{/* ─── Field Data Capture Card ─── */}
						{hasDataCollection && (
							<Col xs={24} sm={12} md={8} lg={6}>
								<Card
									hoverable
									onClick={() => navigate("/field-capture")}
									className="capture-card capture-card-data"
									cover={
										<div className="capture-card-cover capture-cover-data">
											<FileSearchOutlined className="capture-card-icon" />
										</div>
									}
								>
									<Card.Meta
										title="Field Data Capture"
										description="Collect field condition data with AI-assisted extraction"
									/>
								</Card>
							</Col>
						)}

						{/* ─── FacilityLens Card ─── */}
						{hasVisionCaptureAccess && (
							<Col xs={24} sm={12} md={8} lg={6}>
								<Card
									hoverable
									onClick={() => navigate("/facility-lens")}
									className="capture-card capture-card-lens"
									cover={
										<div className="capture-card-cover capture-cover-lens">
											<ScanOutlined className="capture-card-icon" />
										</div>
									}
								>
									<Card.Meta
										title="FacilityLens"
										description="AI-powered asset identification and diagnosis"
									/>
								</Card>
							</Col>
						)}

						{/* ─── Org-specific form cards ─── */}
						{forms.map((form, index) => {
							// Use the organization-specific form ID (form.id) instead of global formId
							const orgFormId = form.id; // Organization-specific form ID
							// Get template form ID (2, 3, 4, 7, etc.) - the global form template ID
							const templateFormId = form.formId || form.form_id;

							// For template form ID 3 (non-org28), only skip if user has Vision Capture access
							// Base users should see the regular Form ID 3
							if (templateFormId === 3 && !isOrg28 && hasVisionCaptureAccess) {
								// Skip form ID 3 for Plus/trial users - they get Vision Capture instead
								return null;
							}

							// Determine form name based on organization and template form ID
							let formName: string;
							if (isOrg28) {
								// Org 28: Use specific names for form IDs 2 and 4
								if (templateFormId === 2) {
									formName = "Handyman Checklist";
								} else if (templateFormId === 4) {
									formName = "QC Checklist";
								} else if (templateFormId === 7) {
									formName = "Property Condition Assessment";
								} else {
									formName =
										form.config?.formName ||
										`Form ${templateFormId || orgFormId}`;
								}
							} else {
								// Other orgs: Use "Inventory & Tagging" for form ID 3
								if (templateFormId === 3) {
									formName = "Inventory & Tagging";
								} else {
									formName =
										form.config?.formName ||
										`Form ${templateFormId || orgFormId}`;
								}
							}

							// Try form logo first, then fall back to org logo
							// Support both camelCase (logoUrl) and snake_case (logo_url) for compatibility
							const config = user.organization?.config as any;
							const logoUrl =
								form.config?.logo ||
								config?.logoUrl ||
								user.organization?.config?.logo_url;

							return (
								<Col xs={24} sm={12} md={8} lg={6} key={orgFormId || index}>
									<Card
										hoverable
										onClick={() => {
											if (orgFormId) {
												navigate(`/forms/${orgFormId}`);
											}
										}}
										cover={
											logoUrl && (
												<div className="form-logo-container">
													<img
														alt="form logo"
														src={logoUrl}
														className="form-logo"
														loading="lazy"
														onError={(e) => {
															console.error("Failed to load logo:", logoUrl);
															e.currentTarget.style.display = "none";
														}}
													/>
												</div>
											)
										}
									>
										<Card.Meta
											title={formName}
											description={form.config?.description || ""}
										/>
									</Card>
								</Col>
							);
						})}
					</Row>
				)}
			</Card>
		</div>
	);
};

export { Forms };
